import Box from '@mui/material/Box';
import { useState } from 'react';
import { brand } from '../../theme';

/** 1500 → "1.5K", 2000000 → "2M" (axis ticks). */
export function compact(n: number): string {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${+(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** A clean max for the y axis and 4 evenly spaced ticks. */
function niceTicks(max: number): number[] {
  if (max <= 0) return [0];
  const raw = max / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? raw;
  return [0, 1, 2, 3, 4].map((i) => i * step);
}

const W = 640;
const H = 240;
const PAD = { top: 12, right: 8, bottom: 28, left: 44 };
const BAR_MAX = 24;
const MUTED = '#667085';
const GRID = '#EAECF0';

/**
 * Single-series column chart (#172): teal columns (≤24px, 4px rounded tops,
 * square at the baseline), hairline grid, compact y ticks, a hover tooltip per
 * column, and the values as a visually hidden table for screen readers.
 */
export function BarChart({ data, title }: { data: { label: string; n: number }[]; title: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const ticks = niceTicks(Math.max(0, ...data.map((d) => d.n)));
  const top = ticks.at(-1) || 1;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const band = plotW / Math.max(1, data.length);
  const barW = Math.min(BAR_MAX, band * 0.6);
  const y = (n: number) => PAD.top + plotH - (n / top) * plotH;
  const hovered = hover != null ? data[hover] : undefined;

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={title}>
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(t)}
              y2={y(t)}
              stroke={GRID}
              strokeWidth={1}
            />
            <text
              x={PAD.left - 6}
              y={y(t)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={11}
              fill={MUTED}
            >
              {compact(t)}
            </text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = PAD.left + i * band + (band - barW) / 2;
          const h = Math.max(0, PAD.top + plotH - y(d.n));
          const r = Math.min(4, h, barW / 2);
          // Rounded data-end, square at the baseline.
          const path =
            h <= 0
              ? ''
              : `M${x},${PAD.top + plotH} V${y(d.n) + r} Q${x},${y(d.n)} ${x + r},${y(d.n)} H${x + barW - r} Q${x + barW},${y(d.n)} ${x + barW},${y(d.n) + r} V${PAD.top + plotH} Z`;
          return (
            <g key={d.label}>
              <path
                data-testid="bar"
                d={path}
                fill={brand.teal}
                opacity={hover == null || hover === i ? 1 : 0.55}
              />
              <text
                x={PAD.left + i * band + band / 2}
                y={H - 8}
                textAnchor="middle"
                fontSize={11}
                fill={MUTED}
              >
                {d.label}
              </text>
              {/* Hit target: the whole band, not just the bar. */}
              <rect
                data-testid="bar-hit"
                x={PAD.left + i * band}
                y={PAD.top}
                width={band}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            </g>
          );
        })}
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={PAD.top + plotH}
          y2={PAD.top + plotH}
          stroke="#D0D5DD"
          strokeWidth={1}
        />
      </svg>
      {hovered && hover != null ? (
        <Box
          role="tooltip"
          sx={{
            position: 'absolute',
            left: `${((PAD.left + hover * band + band / 2) / W) * 100}%`,
            top: 0,
            transform: 'translateX(-50%)',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            fontSize: 13,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            boxShadow: 1,
          }}
        >
          {hovered.label}: {hovered.n.toLocaleString()}
        </Box>
      ) : null}
      <Box
        component="table"
        aria-label={title}
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      >
        <tbody>
          {data.map((d) => (
            <tr key={d.label}>
              <th scope="row">{d.label}</th>
              <td>{d.n.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}
